import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonModal, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ApplicationFacade } from '@ridango/components';
import Env from '@ridango/core/env';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';

export enum DisruptionMessagesModalType {
    CATEGORY = 'CATEGORY',
    DIRECTION = 'DIRECTION',
    ROUTE = 'ROUTE'
}

@Component({
    selector: 'rwidget-disruption-messages-filter-modal',
    templateUrl: './disruption-messages-filter-modal.component.html',
    styleUrls: ['./disruption-messages-filter-modal.component.scss']
})
export class DisruptionMessagesFilterModalComponent implements OnInit, OnDestroy {
    private readonly FILTER_MODAL = 'Filter modal';
    private readonly CLOSE = 'Close';
    private readonly CONFIRM = 'Confirm';
    private readonly SELECT_DESELECT_ALL = 'Select/deselect all';
    private readonly SEARCH = 'SEARCH';

    @ViewChild(IonModal) modal: IonModal;
    @Input() checkboxValues: { [key: string]: boolean };
    @Input() modalType: string;
    disruptionMessagesModalType = DisruptionMessagesModalType;
    allChecked = null;
    allKeys: string[] = [];
    private searchTerm$ = new Subject<string>();
    assetsPath$ = this.applicationFacade.assetsPath$;
    observer: MutationObserver;
    foundResultsAriaDescription: string;

    constructor(
        @Inject('ENV') public env: Env,
        private liveAnnouncer: LiveAnnouncer,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private translateService: TranslateService,
        private analyticsService: AnalyticsService
    ) {}

    get checkboxKeys() {
        return Object.keys(this.checkboxValues);
    }

    ngOnInit() {
        this.makeIonItemsAccessible();
        this.allKeys = Object.keys(this.checkboxValues);
        this.checkIsAllSelect();
        this.foundResultsAriaDescription = this.getFoundResultsAriaDescription();
        this.searchTerm$.pipe(debounceTime(1000)).subscribe((searchTerm) => {
            this.applyFilter(searchTerm);
            this.announceResults();
        });
    }

    ngOnDestroy() {
        this.observer.disconnect();
    }

    get isVastmanlandApp() {
        return this.env.APP_NAME === 'vastmanland-app';
    }

    checkIsAllSelect() {
        this.allChecked = !Object.values(this.checkboxValues).some((val) => !val);
    }

    toggleSelectAll() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_MODAL} - ${this.SELECT_DESELECT_ALL}`
        });
        const allCurrentlySelected = Object.values(this.checkboxValues).every((val) => val);
        for (const key in this.checkboxValues) {
            this.checkboxValues[key] = !allCurrentlySelected;
        }
        this.allChecked = !allCurrentlySelected;
    }

    filterOptions(event: any) {
        const searchTerm = event.target.value.toLowerCase().trim();
        this.searchTerm$.next(searchTerm);
    }

    private applyFilter(searchTerm: string) {
        const terms = searchTerm.split(' ').filter((s) => s);
        const filteredKeys =
            terms.length > 0
                ? this.allKeys.filter((key) => {
                      const lowerCaseKey = key.toLowerCase();
                      const translatedKey = this.translateService.instant(key).toLowerCase();
                      return terms.some((term) => lowerCaseKey.includes(term) || translatedKey.includes(term));
                  })
                : this.allKeys;

        this.checkboxValues = filteredKeys.reduce((obj, key) => {
            obj[key] = this.checkboxValues[key];
            return obj;
        }, {});
    }

    private announceResults() {
        const resultsCount = Object.keys(this.checkboxValues).length;
        const ariaLiveMessage =
            resultsCount > 0
                ? this.translateService.instant('CONTENT_CHANGED_FOUND_RESULTS', { number: resultsCount })
                : this.translateService.instant('EMPTY_STATUS_SEARCH_RESULT_NOT_FOUND');
        this.liveAnnouncer.announce(ariaLiveMessage);
    }

    cancel() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_MODAL} - ${this.CLOSE}`
        });
        return this.modalCtrl.dismiss(null, 'cancel');
    }

    confirm() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_MODAL} - ${this.CONFIRM}`
        });
        return this.modalCtrl.dismiss(this.checkboxValues, 'confirm');
    }

    // FIXME: remove this after upgrading to Ionic 7 where accessibility is improved
    // https://ionicframework.com/docs/api/checkbox#using-the-modern-syntax
    makeIonItemsAccessible() {
        this.observer = new MutationObserver((mutationsList) => {
            mutationsList.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof HTMLElement) {
                            const ionItems =
                                node.tagName.toLowerCase() === 'ion-item'
                                    ? [node]
                                    : Array.from(node.querySelectorAll('ion-item'));
                            ionItems.forEach((ionItem) => {
                                if (ionItem instanceof HTMLElement && ionItem.classList.contains('checkbox-item')) {
                                    const ionCheckbox = ionItem.querySelector('ion-checkbox');
                                    const ionLabel = ionItem.querySelector('ion-label');
                                    if (ionCheckbox) {
                                        const waitForShadowDom = () => {
                                            const input = ionCheckbox.shadowRoot?.querySelector('input');
                                            if (input) {
                                                ionItem.setAttribute('aria-label', ionLabel?.textContent || '');
                                                ionItem.setAttribute('role', 'checkbox');
                                                ionItem.setAttribute('tabindex', '0');
                                                ionCheckbox.setAttribute('aria-hidden', 'true');
                                                ionCheckbox.setAttribute('tabindex', '-1');
                                                input.setAttribute('aria-hidden', 'true');
                                                input.setAttribute('tabindex', '-1');
                                            } else {
                                                setTimeout(waitForShadowDom, 100);
                                            }
                                        };
                                        waitForShadowDom();
                                    }
                                }
                            });
                        }
                    });
                }
            });
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // FIXME: remove this after upgrading to Ionic 7 where accessibility is improved
    // https://ionicframework.com/docs/api/checkbox#using-the-modern-syntax
    toggle($event: Event, option: string) {
        if (!$event.isTrusted) {
            return;
        }

        this.checkboxValues[option] = !this.checkboxValues[option];
        const eventAction = this.checkboxValues[option] ? TrackedEventAction.CHECKED : TrackedEventAction.UNCHECKED;
        this.analyticsService.eventTrack(eventAction, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_MODAL} - ${option}`
        });
        const checked = this.checkboxValues[option];
        const ionCheckbox = $event.target as HTMLElement;
        const ionItem = ionCheckbox?.closest('ion-item');
        const input = ionCheckbox?.shadowRoot?.querySelector('input') as HTMLInputElement | null;

        checked ? input?.setAttribute('checked', '') : input?.removeAttribute('checked');

        if (ionItem) {
            setTimeout(() => {
                ionItem.focus();
            }, 50);
        }
    }

    getFoundResultsAriaDescription() {
        const resultsCount = Object.keys(this.checkboxValues).length;
        return this.translateService.instant('FOUND_N_RESULTS', { count: resultsCount });
    }

    onSearchFieldClick() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_MODAL} - ${this.SEARCH}`
        });
    }
}
